import React, { useRef, useEffect, useMemo } from "react";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import Extent from "@arcgis/core/geometry/Extent";
import ReactGA from "react-ga4";

import * as promiseUtils from "@arcgis/core/core/promiseUtils";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import ScaleBar from "@arcgis/core/widgets/ScaleBar";
import Home from "@arcgis/core/widgets/Home";
import Zoom from "@arcgis/core/widgets/Zoom";
import {
  BaseConfig,
  ModalKeys,
  DraggableKeys,
} from "../../interfaces/config.ts";
import getBasemap from "./getBaseMap.ts";
import { useStore } from "../../stores/store.ts";
import { SimpleMapTooltip } from "../SimpleMapTooltip.tsx";
import { DraggableRoot } from "../Draggables/DraggableRoot.tsx";
import { setupHighlightHitTest } from "./highlightHitTest.ts";
import { loadSecondaryLayers } from "./layerHelpers.ts";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import ZoomSlider from "./CustomWidgets/ZoomSlider.tsx";
import { createPortal } from "react-dom";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";

interface ArcGISMapProps {
  config: BaseConfig;
}

export const ArcGISMap = (props: ArcGISMapProps) => {
  const {
    setMap,
    setMapLoaded,
    setMapView,
    setBufferLayer,
    setMarkupLayer,
    setHighlightLayer,
    setHighlightLayerView,
    setPrimaryLayerView,
    setPrimaryLayer,
    setPrimaryQueryLayer,
    setScale,
    setZoom,
    addExtentToHistory,
    activateTool,
    checkIsLocal,
    setIsMobile,
    setIsTablet,
    setAlternateLayer,
    setAlternateQueryLayer,
    globalSelectedBasemap,
  } = useStore();

  const { isPlanningApp } = props.config;

  const mapOptions = props.config.mapOptions;
  if (
    props.config.authenticationService?.useAuth &&
    !useStore.getState().isAuthenticated
  ) {
    return null;
  }

  const mapRef = useRef(null);
  const zoomSliderDiv = useMemo(() => document.createElement("div"), []);

  useEffect(() => {
    const handleResize = () => {
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const noDisclaimer = params.get("noDisclaimer");
    if (!noDisclaimer) {
      activateTool(ModalKeys.DISCLAIMER);
    }
    if (window.innerWidth >= 768) {
      activateTool(DraggableKeys.LAYERS_DRAGGABLE);
    }
    async function initMap() {
      const basemap = getBasemap(props.config.streetsBasemap);
      const isLocal = await checkIsLocal();
      const googleAnalytics = props.config.googleAnalytics;

      const measurementId = isLocal && googleAnalytics.internalMeasurementId
        ? googleAnalytics.internalMeasurementId
        : googleAnalytics.publicMeasurementId;
      
      if (measurementId) {
        ReactGA.initialize(measurementId);
      }
      else {
        console.warn("No Google Analytics measurement ID provided.");
      }

      const bufferLayer = new GraphicsLayer();
      const highlightLayer = new GraphicsLayer({
        id: "highlight-layer",
      });
      const markupLayer = new GraphicsLayer();

      let primaryLayer;
      let primaryQueryLayer;
      if (props.config.primaryLayer.url) {
        if (props.config.primaryLayer.type === "feature") {
          delete props.config.primaryLayer.type;
          primaryLayer = new FeatureLayer({ ...props.config.primaryLayer });
        } else {
          primaryLayer = new MapImageLayer({
            ...props.config.primaryLayer,
            id: props.config.primaryLayer.id + "-image",
          });
          primaryQueryLayer = new FeatureLayer({
            popupTemplate: props.config.primaryLayer.popupTemplate,
            url: props.config.primaryLayer.url + "/0",
            id: props.config.primaryLayer.id,
            definitionExpression: "1=0",
            legendEnabled: false,
          });
        }
      } else {
        primaryLayer = new FeatureLayer({
          ...props.config.primaryLayer,
          source: [],
          geometryType: "point",
          // objectIdField: "OBJECTID",
          fields: props.config.primaryLayer.fields as any,
          renderer:
            props.config.primaryLayer.rendererOptions?.points?.renderer ||
            props.config.primaryLayer.renderer,
        });

        primaryQueryLayer = new FeatureLayer({
          url: props.config.primaryLayer.queryUrl,
          visible: false,
        });
      }

      // Alternate layer for planning app
      let alternateLayer;
      let alternateQueryLayer;
      if (isPlanningApp && props.config.alternateLayer) {
        if (props.config.alternateLayer.url) {
          if (props.config.alternateLayer.type === "feature") {
            delete props.config.alternateLayer.type;
            alternateLayer = new FeatureLayer({
              ...props.config.alternateLayer,
            });
          } else {
            alternateLayer = new MapImageLayer({
              ...props.config.alternateLayer,
              id: props.config.alternateLayer.id + "-image",
            });
            alternateQueryLayer = new FeatureLayer({
              popupTemplate: props.config.alternateLayer.popupTemplate,
              url: props.config.alternateLayer.url + "/0",
              id: props.config.alternateLayer.id,
              definitionExpression: "1=0",
              legendEnabled: false,
            });
          }
        } else {
          alternateLayer = new FeatureLayer({
            ...props.config.alternateLayer,
            source: [],
            geometryType: "point",
            fields: props.config.alternateLayer.fields as any,
            renderer:
              props.config.alternateLayer.rendererOptions?.points?.renderer ||
              props.config.alternateLayer.renderer,
          });

          alternateQueryLayer = new FeatureLayer({
            url: props.config.alternateLayer.queryUrl,
            visible: false,
          });
        }
      }

      const _layers = [
        primaryLayer,
        highlightLayer,
        bufferLayer,
        markupLayer,
        primaryQueryLayer,
        alternateLayer,
        alternateQueryLayer,
      ].filter(Boolean) as __esri.Layer[];

      const map = new Map({
        basemap: props.config.streetsBasemap ? basemap : "dark-gray-vector",
      });
      map.addMany(_layers);
      loadSecondaryLayers(props.config.secondaryLayers, map);
      setHighlightLayer(highlightLayer);

      setBufferLayer(bufferLayer);
      setMarkupLayer(markupLayer);

      const mapView = new MapView({
        // background: { color: "white" },
        container: mapRef.current || undefined,
        // extent: new Extent(mapOptions.extent),
        constraints: {
          geometry: mapOptions.constraints?.geometry
            ? new Extent(mapOptions.constraints.geometry)
            : undefined,
          // geometry: new Extent(mapOptions.constraints.geometry),
          minZoom: mapOptions.constraints?.minZoom ?? 0,
          maxZoom: mapOptions.constraints?.maxZoom ?? 19,
        },
        map,
        highlights: [
          {
            color: [255, 0, 0, 0.7] as any,
            haloOpacity: 0.9,
            fillOpacity: 0.35,
          },
        ],
      });
      const layerView = await mapView.whenLayerView(primaryLayer);
      setPrimaryLayerView(layerView as __esri.FeatureLayerView);

      // goto extent
      mapView.extent = new Extent(mapOptions.extent)

      const isAerialBasemap = globalSelectedBasemap?.id?.includes("aerial");
      if (!props.config.primaryLayer.queryUrl) {
        mapView.whenLayerView(highlightLayer).then((layerView) => {
          layerView.highlightOptions = {
            color: [255, 0, 0, 1],
            haloOpacity: 1,
            fillOpacity: 0,
          } as any;
          setHighlightLayerView(layerView);
          setupHighlightHitTest({
            highlightLayer,
            highlightLayerView: layerView,
            mapView,
          }); 
        });
      } else {
        mapView.whenLayerView(primaryLayer).then((layerView) => {
          const flv = layerView as __esri.FeatureLayerView;
          setHighlightLayerView(flv);
        });
      }
      mapView.ui.components = [];
      mapView.ui.add(new ScaleBar({ view: mapView }), "bottom-left");
      mapView.ui.add(new Home({ view: mapView }), "top-left");
      mapView.ui.add(new Zoom({ view: mapView }), "top-left");
      // mapView.ui.add(
      //   new DirectionalPad({ view: mapView, visualScale: "s" }),
      //   "top-left"
      // );
      mapView.ui.add(zoomSliderDiv, "top-left");

      // mapView.ui.add(new Print({ view: mapView }), "top-right");
      await mapView.when();

      mapView.extent = mapView.extent.expand(1.2);

      // if (props.config.toolbarOptions?.visibleTools["legend"] === true) {
      // mapView.ui.add(new Legend({ view: mapView }), "top-right");
      // }

      setMapView(mapView);
      setMap(map);
      setPrimaryLayer(primaryLayer);
      setPrimaryQueryLayer(primaryQueryLayer);

      // Alternate layer for planning app
      if (isPlanningApp && alternateLayer && alternateQueryLayer) {
        setAlternateLayer(alternateLayer);
        setAlternateQueryLayer(alternateQueryLayer);
      }

      setZoom(mapView.zoom);
      setScale(mapView.scale);
      await reactiveUtils.whenOnce(() => !mapView.updating);
      setMapLoaded(true);

      reactiveUtils.watch(
        () => mapView.stationary,
        (stationary) => {
          if (stationary) {
            addExtentToHistory(mapView.extent);
          }
        },
      );

      reactiveUtils.watch(
        () => mapView.scale,
        (scale) => {
          setScale(scale);
        },
      );

      reactiveUtils.watch(
        () => mapView.zoom,
        (zoom) => {
          setZoom(zoom);
        },
      );

      if (mapOptions.hoverPopupLayerIds) {
        const hitTestLayers = mapOptions.hoverPopupLayerIds
          .map((id) => map.findLayerById(id))
          .filter(Boolean) as __esri.Layer[];

        let oids = [];

        const performHitTest = promiseUtils.debounce(async (e) => {
          if (!mapView) {
            return;
          }
          const res = await mapView.hitTest(e.native, {
            include: hitTestLayers,
          });
          const hitTestLayer: any = res.results[0]?.layer;

          // If the layer has feature reduction enabled, we don't want to show the popup
          if (
            res.results.length &&
            !hitTestLayer.featureReduction &&
            hitTestLayer.renderer.type !== "heatmap"
          ) {
            const _oids = res.results
              .map((r: any) => r.graphic.attributes[hitTestLayer.objectIdField])
              .sort();
            if (_oids.join(",") !== oids.join(",")) {
              mapView.openPopup({
                features: res.results.map((r: any) => r.graphic),
                location: res.results[0].mapPoint,
              });
            }
          } else {
            mapView.closePopup();
            oids = [];
          }
        });

        mapView.on("pointer-move", (e) => {
          performHitTest(e).catch((err) => {
            if (err.name !== "AbortError") {
              console.error(err);
            }
          });
        });
      }
      // mapView.popup.on("trigger-action", (e: any) => {
      //   if (e.action.id === "crime-stoppers") {
      //     // const attr = mapView.popup.selectedFeature.attributes
      //     // Open a new window with the crime stoppers link
      //     window.open(
      //       "https://www.mchenrysheriff.org/community/crime-stoppers/",
      //       "_blank"
      //     );
      //   }
      // });
    }
    try {
      initMap();
    } catch (err) {
      throw new Error("Error initializing map: " + err);
    }
  }, [mapRef]);

  return (
    <>
      {/* <div
        className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-10"
        style={{ display: useStore.getState().mapLoaded ? "none" : "flex" }}
      >
        <div className="loading loading-bars loading-lg"></div>
      </div> */}
      <main className="h-full" ref={mapRef}></main>
      <SimpleMapTooltip />
      <DraggableRoot />
      {createPortal(
        <ZoomSlider
          minZoom={mapOptions.constraints?.minZoom ?? 2}
          maxZoom={mapOptions.constraints?.maxZoom ?? 15}
        />,
        zoomSliderDiv,
      )}
    </>
  );
};
